import { equals, omit } from 'ramda';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { IJsonQuery } from '../../../../packages/api-models/common/json_query';
import { useApplicationContext } from '../../../common/containers/ApplicationContext';
import { useListingScrollContainer } from '../../../common/containers/ListingScrollContainer';
import { selectJsonQuery } from '../../../common/selectors/filters';
import { readAndRemoveDisablePlaceholderCookie } from '../../../common/utils/searchStringPlaceholder';

/**
 * Пагинация меняет терм `page`, но фильтры при этом те же — на подгрузку страницы реагировать
 * не надо.
 */
const withoutPage = (jsonQuery: IJsonQuery): Omit<IJsonQuery, 'page'> => omit(['page'], jsonQuery);

/**
 * Гасит перебор интентов в поисковой строке.
 *
 * Поводов три: возврат с карточки (сессионная кука), первый скролл выдачи и смена фильтров —
 * в последнем случае пользователь уточнил запрос сам, и перебор подсказок начинает ему
 * противоречить. Признак смены фильтров — только jsonQuery: движение карты в него не попадает.
 *
 * @param isSearchStringVisible полноразмерная строка на экране, то есть перебор интентов видно
 */
export function useDisableSearchStringPlaceholder(isSearchStringVisible: boolean): void {
  const { microfrontendsAPI, logger } = useApplicationContext();
  const scrollContainer = useListingScrollContainer();
  const jsonQuery = useSelector(selectJsonQuery);
  const isDisabledRef = React.useRef(false);
  const initialFiltersRef = React.useRef(withoutPage(jsonQuery));

  const disablePlaceholders = React.useCallback(() => {
    if (isDisabledRef.current || !microfrontendsAPI) {
      return;
    }

    isDisabledRef.current = true;

    microfrontendsAPI.searchString.disablePlaceholders().catch((error: Error) => {
      logger.warning(error, {
        degradation: 'Перебор интентов в поисковой строке не остановлен',
        domain: 'useDisableSearchStringPlaceholder',
      });
    });
  }, [logger, microfrontendsAPI]);

  React.useEffect(() => {
    // Куку читаем только когда есть чем на неё ответить: чтение её удаляет, второго шанса не будет
    if (!microfrontendsAPI || isDisabledRef.current || !readAndRemoveDisablePlaceholderCookie()) {
      return;
    }

    disablePlaceholders();
  }, [disablePlaceholders, microfrontendsAPI]);

  React.useEffect(() => {
    /*
     * Пока полноразмерной строки на экране нет, перебор интентов не виден — микрофронт рисует
     * компактную кнопку. А на входе в посутку сразу предлагается выбрать даты и гостей, то есть
     * фильтры меняются именно в этот момент: без отсечки перебор гас бы, ни разу не показавшись.
     * Поэтому до появления строки просто переносим точку отсчёта.
     */
    if (!isSearchStringVisible) {
      initialFiltersRef.current = withoutPage(jsonQuery);

      return;
    }

    /*
     * Сравниваем с точкой отсчёта по значению, а не по ссылке. Строка запроса тут не годится
     * вовсе: в неё пишутся center и zoom карты, поэтому открытие шторки — карта меняет размер и
     * отдаёт новые bounds — читалось как смена фильтров. А сам jsonQuery после каждого запроса
     * выдачи прилетает из ответа бэка новым объектом с теми же фильтрами.
     */
    if (equals(withoutPage(jsonQuery), initialFiltersRef.current)) {
      return;
    }

    disablePlaceholders();
  }, [disablePlaceholders, isSearchStringVisible, jsonQuery]);

  React.useEffect(() => {
    if (!scrollContainer) {
      return;
    }

    scrollContainer.addEventListener('scroll', disablePlaceholders, { passive: true, once: true });

    return () => {
      scrollContainer.removeEventListener('scroll', disablePlaceholders);
    };
  }, [disablePlaceholders, scrollContainer]);
}
